import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import firebase from '../db/firebase';
import Logo from '../assets/icons/logo2.svg';
import {
  Home, Users2, LogOut, Menu, X,
  PhoneCall, Headset, HandCoins, Package, ShoppingCart, Settings, SendIcon,
  BugIcon, ChevronDown, ChevronRight,
  BookCheck
} from 'lucide-react';
import IconCoin from '../assets/icons/white-coin.svg';

const Nav = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [pendingCounts, setPendingCounts] = useState({});
  const [openSubmenu, setOpenSubmenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const saved = localStorage.getItem('nav-collapsed');
    if (saved !== null) setIsCollapsed(saved === 'true');
    const savedSub = localStorage.getItem('nav-submenu-open');
    if (savedSub !== null) setOpenSubmenu(savedSub === 'true');
  }, []);

  useEffect(() => {
    localStorage.setItem('nav-collapsed', isCollapsed);
  }, [isCollapsed]);

  useEffect(() => {
    localStorage.setItem('nav-submenu-open', openSubmenu);
  }, [openSubmenu]);

  // Top-level (sin los que van al submenu)
  const navItems = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/users', label: 'Users', icon: Users2 },
    { path: '/send-notifications', label: 'Send Notifications', icon: SendIcon },
    { path: '/products', label: 'Products', icon: ShoppingCart },
    { path: '/app-settings', label: 'App Settings', icon: Settings },
    { path: '/bug-report', label: 'Bug Report', icon: BugIcon },
  ];

  // Ítems del submenu (incluye Request a Call)
  const submenuItems = [
    { path: '/requests', label: 'Request a Call', icon: PhoneCall },
    { path: '/remote-surveys', label: 'Remote Surveys', icon: Headset },
    { path: '/requests-money', label: 'Request Money', icon: HandCoins },
    { path: '/requests-products', label: 'Request Products', icon: Package },
    { path: '/purchase-points', label: 'Purchase Points', icon: null, iconSvg: IconCoin },
  ];

  // Suscripciones badges
  useEffect(() => {
    const collections = [
      { key: '/requests', name: 'requests' },
      { key: '/remote-surveys', name: 'remoteSurveys' },
      { key: '/requests-money', name: 'requestsMoney' },
      { key: '/requests-products', name: 'requestsProduct' },
      { key: '/purchase-points', name: 'requestPoint' },
    ];

    const unsubscribes = collections.map(({ key, name }) => {
      const q = query(collection(firebase.db, name), where('state', '==', 'pending'));
      return onSnapshot(q, snapshot => {
        setPendingCounts(prev => ({ ...prev, [key]: snapshot.size }));
      });
    });

    return () => unsubscribes.forEach(unsub => unsub());
  }, []);

  const handleLogout = async () => {
    const confirm = window.confirm('Are you sure you want to sign out?');
    if (!confirm) return;
    try {
      await firebase.auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error.message);
      alert('Sign-out failed. Please try again.');
    }
  };

  const isActive = (path) => location.pathname === path;

  // Total del grupo
  const submenuTotal = useMemo(
    () => submenuItems.reduce((acc, it) => acc + (pendingCounts[it.path] || 0), 0),
    [pendingCounts]
  );

  const renderLinks = () => (
    <div className="flex-1 overflow-y-auto">
      <nav className="flex flex-col gap-2 p-2">
        {/* Top-level */}
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setIsMobileOpen(false)}
              title={isCollapsed ? item.label : ''}
              className={`relative flex items-center gap-3 px-4 py-2 rounded hover:bg-gray-700 transition ${active ? 'bg-gray-700' : ''}`}
            >
              <Icon size={20} className={active ? 'text-white' : 'text-gray-300'} />
              {!isCollapsed && (
                <span className="flex items-center gap-2">
                  {item.label}
                  {pendingCounts[item.path] > 0 && (
                    <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5 min-w-[20px] text-center">
                      {pendingCounts[item.path]}
                    </span>
                  )}
                </span>
              )}
            </Link>
          );
        })}

        {/* Submenu */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpenSubmenu(v => !v)}
            title={isCollapsed ? 'Requests & Purchases' : ''}
            className={`w-full relative flex items-center gap-3 px-4 py-2 rounded hover:bg-gray-700 transition ${
              submenuItems.some(i => isActive(i.path)) ? 'bg-gray-700' : ''
            }`}
          >
            <BookCheck size={20} className="text-gray-300" />
            {!isCollapsed && (
              <span className="flex items-center gap-2 flex-1 text-left">
                All Requests
                {submenuTotal > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5 min-w-[20px] text-center">
                    {submenuTotal}
                  </span>
                )}
              </span>
            )}
            {!isCollapsed && (
              <span className="ml-auto">
                {openSubmenu ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </span>
            )}
          </button>

          {/* Submenu expandido (sidebar normal) */}
          {!isCollapsed && (
            <div
              className={`grid transition-all duration-300 overflow-hidden ml-2 ${openSubmenu ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
            >
              <div className="min-h-0">
                <ul className="mt-1 rounded-lg">
                  {submenuItems.map((item) => {
                    const Icon = item.icon;
                    const IconSvg = item.iconSvg;
                    const active = isActive(item.path);
                    return (
                      <li key={item.path}>
                        <Link
                          to={item.path}
                          onClick={() => setIsMobileOpen(false)}
                          className={`relative flex items-center gap-3 pl-10 pr-4 py-2 rounded hover:bg-gray-700 transition ${active ? 'bg-gray-700' : ''}`}
                        >
                          {IconSvg ? (
                            <img src={IconSvg} alt={item.label} className="w-5 h-5 opacity-90" />
                          ) : (
                            <Icon size={18} className={active ? 'text-white' : 'text-gray-300'} />
                          )}
                          <span className="flex items-center gap-2">
                            {item.label}
                            {pendingCounts[item.path] > 0 && (
                              <span className="bg-red-500 text-white text-[10px] rounded-full px-2 py-[2px] min-w-[18px] text-center">
                                {pendingCounts[item.path]}
                              </span>
                            )}
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}

          {/* Submenu flyout (sidebar colapsada) */}
          {isCollapsed && openSubmenu && (
            <div className="absolute left-full top-0 ml-2 w-56 bg-gray-800 rounded-lg shadow-lg py-2 z-50">
              <ul className="max-h-[60vh] overflow-y-auto">
                {submenuItems.map((item) => {
                  const Icon = item.icon;
                  const IconSvg = item.iconSvg;
                  const active = isActive(item.path);
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        onClick={() => {
                          setIsMobileOpen(false);
                          setOpenSubmenu(false);
                        }}
                        className={`relative flex items-center gap-3 px-4 py-2 hover:bg-gray-700 ${active ? 'bg-gray-700' : ''}`}
                      >
                        {IconSvg ? (
                          <img src={IconSvg} alt={item.label} className="w-5 h-5 opacity-90" />
                        ) : (
                          <Icon size={18} className={active ? 'text-white' : 'text-gray-300'} />
                        )}
                        <span className="flex items-center gap-2">
                          {item.label}
                          {pendingCounts[item.path] > 0 && (
                            <span className="bg-red-500 text-white text-[10px] rounded-full px-2 py-[2px] min-w-[18px] text-center">
                              {pendingCounts[item.path]}
                            </span>
                          )}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </nav>
    </div>
  );

  return (
    <>
      {/* Mobile burger */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setIsMobileOpen(true)}
          aria-label="Open menu"
          className="bg-blueBlack text-white p-2 rounded"
        >
          <Menu size={24} />
        </button>
      </div>

      {/* Overlay mobile */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed z-50 h-screen bg-blueBlack text-white flex flex-col justify-between shadow-lg transition-all duration-300
          ${isMobileOpen ? 'left-0' : 'left-[-100%]'}
          w-[260px] lg:static lg:left-0
          ${isCollapsed ? 'lg:w-16' : 'lg:w-[260px]'}
        `}
      >
        <div className="min-h-0 flex flex-col">
          {/* Toggle collapse */}
          <div className="relative mb-16 hidden lg:block">
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              aria-label="Toggle navigation"
              className="absolute top-0 right-0 p-4 text-xl font-bold focus:outline-none"
            >
              {isCollapsed ? <Menu size={24} /> : <X size={24} />}
            </button>
          </div>

          {/* Logo */}
          <div className="flex items-center justify-center h-16 mb-10">
            <img
              src={Logo}
              alt="Logo"
              className={`h-10 transition-all duration-300 ${isCollapsed ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
              style={{ transitionProperty: 'opacity, transform' }}
            />
          </div>

          {/* Links (scrollable) */}
          {renderLinks()}
        </div>

        {/* Footer */}
        <div className="p-4">
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 rounded hover:bg-red-600 bg-red-500 transition w-full"
          >
            <LogOut size={24} />
            {!isCollapsed && <span>Logout</span>}
          </button>
          <p className='text-center text-gray-500 mt-2 text-sm'>v1.4.0</p>
        </div>
      </div>
    </>
  );
};

export default Nav;